import { useState, useEffect } from 'react';
import { getQCSearchHistory, createQCSearchHistory, deleteQCSearchHistory } from '@/services/promotionService';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function QCSearchHistoryList() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [productName, setProductName] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await getQCSearchHistory();
      setItems(data);
    } catch (e) {
      toast.error('Failed to load items.');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleAdd = async () => {
    if (!query || !productName) {
      toast.error('Query and Product Name are required.');
      return;
    }
    try {
      await createQCSearchHistory(query, productName, imageUrl);
      toast.success('Added successfully.');
      setQuery('');
      setProductName('');
      setImageUrl('');
      fetchItems();
    } catch (e) {
      toast.error('Failed to add query history.');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteQCSearchHistory(id);
      toast.success('Deleted successfully.');
      fetchItems();
    } catch (e) {
      toast.error('Failed to delete.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">QC Search History</h1>
        <p className="text-muted-foreground">Manage and view the tracked QC search results.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add Manual Record</CardTitle>
          <CardDescription>Manually insert a search history row</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-4 items-center">
          <Input 
            placeholder="Query" 
            value={query} 
            onChange={(e) => setQuery(e.target.value)} 
          />
          <Input 
            placeholder="Product Name" 
            value={productName} 
            onChange={(e) => setProductName(e.target.value)} 
          />
          <Input 
            placeholder="Image URL (optional)" 
            value={imageUrl} 
            onChange={(e) => setImageUrl(e.target.value)} 
          />
          <Button onClick={handleAdd} className="w-full sm:w-auto"><Plus className="w-4 h-4 mr-2" /> Add</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>{loading ? 'Loading...' : `Showing ${items.length} records`}</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Query</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Image</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{item.query}</TableCell>
                  <TableCell>{item.product_name}</TableCell>
                  <TableCell>
                    {item.image_url ? (
                      <img src={item.image_url} alt="pic" className="w-8 h-8 rounded object-cover" />
                    ) : (
                       <span className="text-muted-foreground text-xs">No image</span>
                    )}
                  </TableCell>
                  <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(item.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No QC search history found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
